import {
  NotificationParams,
  useNotification,
} from "@/providers/notifications-bot-actions/notifications-bot-actions";
import { api, type Api } from "@api/bot-base-api-request";
import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";

// Native Token Transfer Hook
const BOT_TRANSFER_NATIVE_QUERY_KEY = "bot-transfer-native";

type BotTransferNativeParams = {
  appPubKey: string;
  to: string;
  amount: string;
  walletAddress: string;
  userId: string;
  chainId: number;
};

const postBotTransferNative = async (
  api: Api,
  params: BotTransferNativeParams
) => api.post("transfer/transferNative", { json: params }).json();

const useBotTransferNativeMutation = () =>
  useMutation({
    mutationFn: async (params: BotTransferNativeParams) =>
      postBotTransferNative(api, params),
    mutationKey: [BOT_TRANSFER_NATIVE_QUERY_KEY],
    onSuccess: () => {
      toast.success("Transfer native token successfully");
    },
    onError: (error) => {
      toast.error("Transfer native token failed. Reason: " + error.message);
    },
  });

// Token Transfer Hook
const BOT_TRANSFER_TOKEN_QUERY_KEY = "bot-transfer-token";

type BotTransferTokenParams = {
  appPubKey: string;
  to: string;
  amount: string;
  walletAddress: string;
  userId: string;
  tokenAddress: string;
  chainId: number;
};

type BotTransferTokenInput = {
  params: BotTransferTokenParams;
  notifyParams: NotificationParams;
};

const postBotTransferToken = async (api: Api, params: BotTransferTokenParams) =>
  api.post("transfer/transferToken", { json: params }).json();

const useBotTransferTokenMutation = () => {
  const { setNotifyProps } = useNotification();

  return useMutation<unknown, Error, BotTransferTokenInput>({
    mutationFn: async (input: BotTransferTokenInput) =>
      await postBotTransferToken(api, input.params),
    mutationKey: [BOT_TRANSFER_TOKEN_QUERY_KEY],
    onSuccess: async (_data, variables) => {
      setNotifyProps({
        type: "success",
        action: "sendToken",
        params: {
          amount: variables.notifyParams.amount,
          tokenSymbol: variables.notifyParams.tokenSymbol,
          walletAddress: variables.notifyParams.walletAddress,
        } as NotificationParams,
      });
    },
    onError: async (_error, variables) => {
      setNotifyProps({
        type: "error",
        action: "sendToken",
        params: {
          amount: variables.notifyParams.amount,
          tokenSymbol: variables.notifyParams.tokenSymbol,
          walletAddress: variables.notifyParams.walletAddress,
        } as NotificationParams,
      });
    },
  });
};

export {
  useBotTransferNativeMutation,
  postBotTransferNative,
  BOT_TRANSFER_NATIVE_QUERY_KEY,
  useBotTransferTokenMutation,
  postBotTransferToken,
  BOT_TRANSFER_TOKEN_QUERY_KEY,
};
